import { useEffect, useState } from 'react'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { Typography, Tooltip } from 'antd'
import styled from 'styled-components'
import { colours, sizes } from '@qflow/theme'

import {
  SelectMeter,
  UTILITIES,
  UtilitySelector
} from 'portal/components/Utilities/common'
import { ColourButton, icons, Spacer, Spinner } from 'portal/lib/primitives'

import { projectUsersLoad } from 'portal/store/projectUsers/actions'

import { Meter } from 'portal/components/Utilities/Meters/Interface'
import { useMetersLoader } from 'portal/components/Utilities/Meters/useMetersLoader'

import { Reading } from './Interface'
import { NewReadingPanel } from './NewReadingPanel'
import { ReadingsTable } from './ReadingsTable'
import { useReadingssLoader } from './useReadingsLoader'

export const Readings = () => {
  const [selectedUtility, setSelectedUtility] = useState(UTILITIES.WATER)
  const [selectedMeterId, setSelectedMeterId] = useState('')

  const [showNewReadingsPanel, setShowNewReadingsPanel] = useState(false)

  const { accountId, projectId } = useParams<{
    accountId: string
    projectId: string
  }>()

  const {
    loading: metersLoading,
    error: metersError,
    reload: metersReload,
    readOnly: metersReadOnly
  } = useMetersLoader()
  const {
    loading: readingsLoading,
    error: readingsError,
    reload: readingsReload,
    readOnly: readingsReadOnly
  } = useReadingssLoader()

  const loading = metersLoading || readingsLoading
  const error = metersError || readingsError

  const dispatch = useDispatch()

  // Load Project Users in to Redux state
  useEffect(() => {
    dispatch(projectUsersLoad(accountId, projectId, false))
  }, [accountId, dispatch, projectId])

  const { Title } = Typography

  const allMeters: { data: Meter[] } = useSelector(
    (state: RootStateOrAny) => state.meters
  )
  const allReadings: { data: Reading[] } = useSelector(
    (state: RootStateOrAny) => state.readings
  )

  // Split Meters into water and electricity
  let waterMeters: Meter[] = []
  let electricityMeters: Meter[] = []

  if (allMeters.data && allMeters.data.length > 0) {
    const tempMeters = [...allMeters.data]
    tempMeters.sort((a: Meter, b: Meter) => {
      return a.meterName.localeCompare(b.meterName)
    })

    waterMeters = tempMeters.filter(
      (meter: Meter) =>
        meter.utilityType.toLowerCase() === UTILITIES.WATER.toLowerCase()
    )
    electricityMeters = tempMeters.filter(
      (meter: Meter) =>
        meter.utilityType.toLowerCase() === UTILITIES.ELECTRICITY.toLowerCase()
    )
  }

  // Split Readings into water and electricity
  let waterReadings: Reading[] = []
  let electricityReadings: Reading[] = []

  if (allReadings.data && allReadings.data.length > 0) {
    const tempReadings = [...allReadings.data]
    tempReadings.sort((a: Reading, b: Reading) => {
      return b.readingDateTime.localeCompare(a.readingDateTime)
    })

    waterReadings = tempReadings.filter((reading: Reading) =>
      waterMeters.find((meter: Meter) => meter.meterId === reading.meterId)
    )

    electricityReadings = tempReadings.filter((reading: Reading) =>
      electricityMeters.find(
        (meter: Meter) => meter.meterId === reading.meterId
      )
    )
  }

  let noReadings = true

  if (selectedUtility === UTILITIES.WATER) {
    noReadings = waterReadings.length === 0
  } else {
    noReadings = electricityReadings.length === 0
  }

  const handleUtilityChange = (utility: string) => {
    // Reset selected meter when utility changes
    setSelectedUtility(utility)
    setSelectedMeterId('')
    setShowNewReadingsPanel(false)
  }

  const handleSelectedMeterChange = (meterId: string) => {
    setSelectedMeterId(meterId)
    setShowNewReadingsPanel(false)
  }

  if (error) {
    console.error('Error: ', error)
    return (
      <CenterPanel>
        <div>Error: {error.message}</div>
      </CenterPanel>
    )
  }

  return (
    <>
      <TitleBar>
        <LeftSide>
          <UtilitySelector
            selectedUtility={selectedUtility}
            onChange={handleUtilityChange}
          />
          <SelectMeter
            selectedMeterId={selectedMeterId}
            setSelectedMeterId={handleSelectedMeterChange}
            meters={
              selectedUtility === UTILITIES.WATER
                ? waterMeters
                : electricityMeters
            }
          />
        </LeftSide>
        <ButtonContainer>
          <Tooltip title={'Coming soon'} placement="left">
            <div>
              <ColourButton.Basic
                id="ImportReadings"
                onClick={() => {
                  // Placeholder
                }}
                disabled={true}
              >
                <icons.Upload size={sizes.SMALL} />
                <Spacer.Fine />
                Import readings
              </ColourButton.Basic>
            </div>
          </Tooltip>
          <Tooltip
            title={
              readingsReadOnly
                ? 'You do not have permission to add or edit readings.'
                : null
            }
            placement="left"
          >
            <div>
              <ColourButton.Action
                id="NewReading"
                onClick={() => {
                  setShowNewReadingsPanel(true)
                }}
                disabled={readingsReadOnly || showNewReadingsPanel}
              >
                <icons.Plus size={sizes.SMALL} />
                <Spacer.Fine />
                New reading
              </ColourButton.Action>
            </div>
          </Tooltip>
        </ButtonContainer>
      </TitleBar>

      {loading && (
        <CenterPanel>
          <Spinner />
        </CenterPanel>
      )}

      <ScrollWrapper>
        {showNewReadingsPanel && (
          <NewReadingPanel
            selectedUtility={selectedUtility}
            selectedMeterId={selectedMeterId}
            setShowNewReadingsPanel={setShowNewReadingsPanel}
            meters={
              selectedUtility === UTILITIES.WATER
                ? waterMeters
                : electricityMeters
            }
            readings={
              selectedUtility === UTILITIES.WATER
                ? waterReadings
                : electricityReadings
            }
            metersReadOnly={metersReadOnly}
            metersReload={metersReload}
            readingsReload={readingsReload}
          />
        )}

        {!loading && !noReadings && (
          <ReadingsTable
            meters={
              selectedUtility === UTILITIES.WATER
                ? waterMeters
                : electricityMeters
            }
            readings={
              selectedUtility === UTILITIES.WATER
                ? waterReadings
                : electricityReadings
            }
            utilityType={selectedUtility}
            selectedMeterId={selectedMeterId}
            readingsReadOnly={readingsReadOnly}
            readingsReload={readingsReload}
          />
        )}

        {!loading && noReadings && !showNewReadingsPanel && (
          <CenterPanel>
            <Title level={3}>No readings yet</Title>
            <p>
              <Typography.Text>
                Add your first reading or bulk import readings
              </Typography.Text>
            </p>
            <ButtonContainer>
              <Tooltip title={'Coming soon'} placement="left">
                <div>
                  <ColourButton.Basic
                    id="ImportReadings"
                    onClick={() => {
                      // Placeholder
                    }}
                    disabled={true}
                  >
                    <icons.Upload size={sizes.SMALL} />
                    <Spacer.Fine />
                    Import readings
                  </ColourButton.Basic>
                </div>
              </Tooltip>
              <Tooltip
                title={
                  readingsReadOnly
                    ? 'You do not have permission to add or edit readings.'
                    : null
                }
                placement="left"
              >
                <div>
                  <ColourButton.Action
                    id="NewReading"
                    onClick={() => {
                      setShowNewReadingsPanel(true)
                    }}
                    disabled={readingsReadOnly}
                  >
                    <icons.Plus size={sizes.SMALL} />
                    <Spacer.Fine />
                    New reading
                  </ColourButton.Action>
                </div>
              </Tooltip>
            </ButtonContainer>
          </CenterPanel>
        )}
      </ScrollWrapper>
    </>
  )
}

const TitleBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: 0 1rem 0 0.625rem;
  margin: 0 0 1rem -0.5rem;
  box-shadow: 0px 1px 3px 0px #00000026;
  background-color: ${colours.WHITE};
  border-radius: 10px;

  h3 {
    margin: 0;
  }
`
const LeftSide = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`
const ButtonContainer = styled.div`
  display: flex;
  gap: 0.625rem;
`
const ScrollWrapper = styled.div`
  overflow-y: auto;
  height: calc(100vh - 160px);
  margin: 0 -0.75rem 0 -0.5rem;
  padding: 0 0.75rem 0 0.5rem;
`

const CenterPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: calc(100vh - 300px);
  min-height: 150px;

  p {
    margin-bottom: 1.25rem;
  }
`
